import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Send, Loader2 } from "lucide-react";
import { chat } from "../lib/api";
import { Portrait, YearPicker } from "../components/Portrait";
import { useFutureSelf } from "../lib/futureSelf";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "I'm an imagined, older version of you. Ask me anything — about money, work, love, regrets. I'll answer honestly, but remember: I'm a possibility, not a prophecy.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const fs = useFutureSelf(id);
  const { ensureAged } = fs;

  useEffect(() => { ensureAged(); }, [ensureAged]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setLoading(true);
    try {
      const { reply } = await chat(id, text, next.slice(-6), fs.years);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch {
      setMessages([...next, { role: "assistant", content: "The echo stuttered. Try asking again." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "What did you regret most?",
    "How did you handle money in your 30s?",
    "Should I take the leap now?",
  ];

  return (
    <div className="min-h-screen px-5 md:px-8 py-6 max-w-3xl mx-auto flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(`/journey/${id}`)} className="btn-pill text-xs" data-testid="chat-back-btn">
          <ChevronLeft size={14} /> Journey
        </button>
        <div className="font-mono-label text-white/50">Talk to Future You</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="flex items-center gap-4 mb-6 glass rounded-2xl p-5 flex-wrap">
        <Portrait src={fs.agedUrl} size={72} gold loading={fs.aging} testId="chat-portrait" />
        <div className="flex-1 min-w-[160px]">
          <div className="font-display text-lg">Future You · {fs.futureAgeLabel()}</div>
          <div className="text-xs text-white/50">
            {fs.photoUrl ? "An imagined version · not a prediction" : "Upload a photo on your journey page to see yourself aged"}
          </div>
        </div>
        <YearPicker years={fs.years} setYears={fs.setYears} labelFor={fs.futureAgeLabel} />
      </div>

      <div className="flex-1 space-y-4 pb-32" data-testid="chat-messages">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-cyan-400/10 border border-cyan-400/30 text-white"
                  : "glass border border-amber-400/25 text-white/90"
              }`}
              data-testid={`chat-message-${i}`}
            >
              {m.content}
            </div>
          </motion.div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="glass border border-amber-400/25 rounded-2xl px-4 py-3 text-sm text-white/60 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Future You is thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 px-5 md:px-8 py-5 bg-gradient-to-t from-[#05050A] via-[#05050A] to-transparent">
        <div className="max-w-3xl mx-auto">
          {messages.length < 3 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white/60 hover:border-amber-400/40 hover:text-white"
                  data-testid={`chat-suggestion-${s.slice(0, 8)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 glass rounded-full p-1.5 pl-5">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask your future self…"
              data-testid="chat-input"
              className="flex-1 bg-transparent focus:outline-none text-white placeholder-white/35 py-2"
            />
            <button
              onClick={send}
              disabled={loading}
              data-testid="chat-send-btn"
              className="btn-pill btn-pill-primary !py-2 !px-4"
            >
              <Send size={14} />
            </button>
          </div>
          <p className="text-[10px] text-white/30 text-center mt-2">
            Responses are imaginative scenarios, not predictions.
          </p>
        </div>
      </div>
    </div>
  );
}
